
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Cookie, X } from 'lucide-react';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if the user has already accepted cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    
    // If not, show the banner after a short delay
    if (!cookieConsent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAllCookies = () => {
    // Save consent in localStorage
    localStorage.setItem('cookieConsent', 'all');
    
    // Set analytics cookies
    document.cookie = "analytics_enabled=true; max-age=31536000; path=/";
    document.cookie = "marketing_enabled=true; max-age=31536000; path=/";
    
    // Log this event for admin analytics
    logConsentAction('accepted_all');
    
    // Hide the banner
    setIsVisible(false);
  };

  const acceptEssentialOnly = () => {
    // Save limited consent in localStorage
    localStorage.setItem('cookieConsent', 'essential');
    
    // Set only essential cookies
    document.cookie = "analytics_enabled=false; max-age=31536000; path=/";
    document.cookie = "marketing_enabled=false; max-age=31536000; path=/";
    
    // Log this event for admin analytics
    logConsentAction('essential_only');
    
    // Hide the banner
    setIsVisible(false);
  };

  const logConsentAction = (action: string) => {
    // In a real app, this would send data to an analytics endpoint
    // For now, we'll just store it in localStorage for the admin panel to read
    const consentData = JSON.parse(localStorage.getItem('cookieConsentData') || '[]');
    
    consentData.push({
      action,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      path: window.location.pathname
    });
    
    localStorage.setItem('cookieConsentData', JSON.stringify(consentData));
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg z-50 border-t border-gray-200 p-4 md:p-6">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <Cookie className="h-6 w-6 text-apple-blue flex-shrink-0 mt-1" />
            <div>
              <h3 className="text-base font-medium text-apple-text mb-1">We use cookies</h3>
              <p className="text-sm text-apple-text-secondary mb-2">
                True Talent Apply uses cookies to enhance your browsing experience, analyze site traffic, and personalize content.
                See our <Link to="/cookie-policy" className="text-apple-blue hover:underline">Cookie Policy</Link> for more information.
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={acceptEssentialOnly}
              className="whitespace-nowrap"
            >
              Essential Only
            </Button>
            <Button
              size="sm"
              onClick={acceptAllCookies}
              className="whitespace-nowrap"
            >
              Accept All
            </Button>
          </div>
          
          <button
            onClick={() => setIsVisible(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
            aria-label="Close cookie banner"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
